// date-fns supports Norwegian Bokmål
import { fi, sv, nb } from 'date-fns/locale';
import { format } from 'date-fns';
import useI18n from "./useI18n";

const useFormatter = () => {
    const { getLocale, getLocaleBCP47 } = useI18n();

    /**
     * Format date to locale specific format.
     * @param {string} str
     * @param {string|undefined} formatByCountry
     * @returns {string} - date in locale specific format
     */
    const formatDate = (str, formatByCountry = undefined) => {
        let date;

        // format is not given as a parameter
        if (formatByCountry === undefined) {
            formatByCountry = getLocale();
        }

        // get correct localize property, use fi format as fallback
        switch (formatByCountry) {
            case 'se':
                formatByCountry = sv;
                break;
            case 'no':
                formatByCountry = nb;
                break;
            case 'fi':
            default:
                formatByCountry = fi;
                break;
        }

        // use date-fns format() to localize the date
        if (formatByCountry === fi) {
            // use date-fns pattern 'dd.MM.yyyy' for Finland
            date = format(new Date(str), 'dd.MM.yyyy', { locale: formatByCountry });
        } else {
            // for other countries use date-fns pattern 'P' for formatting (long localized date)
            date = format(new Date(str), 'P', { locale: formatByCountry });
        }

        return date;
    }

    /**
     * Format time to locale specific format, show only hours and minutes.
     *
     * Can be given country as parameter, but if left undefined,
     * uses operative country from localstorage.
     *
     * @param {string} str UTC timestamp
     * @param {string|undefined} formatByCountry
     * @returns string - time in locale specific format
     */
    const formatTime = (str, formatByCountry = undefined) => {

        if (formatByCountry === undefined) {
            formatByCountry = getLocale();
        }

        // get correct localize property, use fi format as fallback
        switch (formatByCountry) {
            case 'se':
                formatByCountry = sv;
                break;
            case 'no':
                formatByCountry = nb;
                break;
            case 'fi':
            default:
                formatByCountry = fi;
                break;
        }

        return format(new Date(str), 'p', { locale: formatByCountry });
    }

    /**
     * Format money value to locale specific format.
     * @param {number} value
     * @param {string} currency
     * @param {string|undefined} locale
     * @returns {*|string}
     */
    const formatCurrency = (value, currency, locale = undefined) => {
        if (value === undefined || value === null) {
            return '';
        }

        if (locale === undefined || locale === null) {
            locale = getLocaleBCP47().replace('_', '-');
        }

        // Currency has to be three-letter code, otherwise use fallback (usually env variable value)
        if (currency.length === 3) {
            return value.toLocaleString(locale, {
                style: 'currency',
                currency: currency.toUpperCase()
            });
        }

        return value;
    }

    return {
        formatDate,
        formatTime,
        formatCurrency
    }
}

export default useFormatter;
